import React from 'react';
import bind from '@reverbdotcom/commons/src/bind';
import OfferCreateForm from '@reverbdotcom/commons/src/offers/offer_create_form';
import { UserInputOffer } from '@reverbdotcom/commons/src/offers/negotiation_types';
import Modal from '@reverbdotcom/commons/src/components/modal';
import { ConnectedOfferListingSidebar } from '@reverbdotcom/commons/src/offers/offer_listing_sidebar';
import { IPhotoAttrs } from '../photos/photo';
import {
  core_apimessages_Channel,
  core_apimessages_CreateNegotiationRequest,
} from '../../schemas/rql';
import { ControlledBodyInput } from './body_input';
import URLHelpers from '../shared/url_helpers';
import I18n from 'i18n-js';

export interface IProps {
  recipientUuid: string;
  listingId?: string;
  isSeller: boolean;
  canIncludeOffer: boolean;
  photoUploadOptions?: Record<string, any>;
  hideListingSearch?: boolean;
}

export interface IInput {
  message: string;
  price?: string;
  shippingPrice?: string;
  quantity?: number;
}

export interface IState {
  isOfferModalOpen: boolean;
  input: IInput;
  photos: IPhotoAttrs[];
}

// Wraps the BodyInput and CreateOfferButton components, and syncs the state between them,
// so if you start typing a message and then decide to include an offer, your message
// (and photos) persists to the offer modal.
export default class MessageForm extends React.Component<IProps, IState> {
  state: IState = {
    isOfferModalOpen: false,
    input: {
      message: '',
    },
    photos: [],
  };

  @bind handleMessageChange(message) {
    this.setState({
      input: {
        ...this.state.input,
        message,
      },
    });
  }

  @bind handlePhotosChange(photos) {
    this.setState({ photos });
  }

  @bind
  handleOfferChange(offer: Partial<UserInputOffer>) {
    this.setState({
      input: {
        ...this.state.input,
        ...offer,
      },
    });
  }

  @bind
  toggleOfferModal() {
    this.setState({
      isOfferModalOpen: !this.state.isOfferModalOpen,
    });
  }

  @bind
  handleCloseOfferModal() {
    this.setState({ isOfferModalOpen: false });
  }

  @bind
  handleSuccess() {
    this.setState({ isOfferModalOpen: false });
    window.location.reload();
  }

  get additionalMutationParams(): Partial<core_apimessages_CreateNegotiationRequest> {
    return {
      message: this.state.input.message,
      conversation: {
        listingId: String(this.props.listingId),
        photos: this.photoData,
      },
    };
  }

  get photoData() {
    return this.state.photos.map(photo => ({
      publicId: photo.publicId,
      version: photo.version && String(photo.version),
      width: photo.width,
      height: photo.height,
      format: photo.format,
      resourceType: photo.resourceType,
      originalFilename: photo.originalFilename,
      transformation: photo.transformation,
    }));
  }

  get sharedBodyInputProps() {
    return {
      message: this.state.input.message,
      onChange: this.handleMessageChange,
      photos: this.state.photos,
      onPhotosChange: this.handlePhotosChange,
      productId: this.props.listingId,
      isSeller: this.props.isSeller,
      photoUploadOptions: this.props.photoUploadOptions,
      hideListingSearch: this.props.hideListingSearch,
    };
  }

  renderOfferMessageInput() {
    return (
      <ControlledBodyInput
        {...this.sharedBodyInputProps}
        isRequired={false}
        inputNamespace=""
        data-offer-body-input
      />
    );
  }

  renderOfferSidebar() {
    return (
      <ConnectedOfferListingSidebar
        isDisplayed={this.state.isOfferModalOpen}
        isSeller={this.props.isSeller}
        listingId={this.props.listingId}
        shopChannel={core_apimessages_Channel.REVERB}
        listingUrlGenerator={URLHelpers.listingPagePath}
      />
    );
  }

  renderIncludeOffer() {
    if (!this.props.canIncludeOffer) { return null; }

    return (
      <div>
        <button
          className="button button--primary width-100"
          onClick={this.toggleOfferModal}
          type="button"
          data-include-offer-button
        >
          {I18n.t('discovery.messages.messageForm.includeOffer')}
        </button>
        <Modal
          onRequestClose={this.handleCloseOfferModal}
          isOpen={this.state.isOfferModalOpen}
          headerTitle={I18n.t('discovery.messages.messageForm.offerModalHeader')}
          sidebarContent={this.renderOfferSidebar()}
          paddedBody
          newClose
          data-offer-modal
        >
          <OfferCreateForm
            isSeller={this.props.isSeller}
            recipientUuid={this.props.recipientUuid}
            listingId={this.props.listingId}
            shopChannel={core_apimessages_Channel.REVERB}
            onSuccess={this.handleSuccess}
            onCancel={this.handleCloseOfferModal}
            isDisplayed={this.state.isOfferModalOpen}
            offer={this.state.input}
            onChange={this.handleOfferChange}
            messageInput={this.renderOfferMessageInput()}
            additionalMutationParams={this.additionalMutationParams}
          />
        </Modal>
      </div>
    );
  }

  render() {
    return (
      <div className="g-container">
        <div className="g-col-8 g-col-mobile-12">
          <ControlledBodyInput
            {...this.sharedBodyInputProps}
            isRequired
            inputNamespace="message"
            data-message-body-input
          />
        </div>
        <div className=" g-col-4 g-col-mobile-12 mb-1 mobile-mt-1">
          {this.renderIncludeOffer()}
        </div>
      </div>
    );
  }
}
